import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { RequestParams, ApiResponse } from '@/shared/api';
import type { ApiResponseList } from '@/shared/api/types';
import { PRODUCT_KEYS } from '../lib';
import { ProductApi } from '../api';
import type {
  ProductBase,
  ProductById,
  ProductCreatePayload,
  ProductUpdatePayload,
  ProductItem,
  ProductItemListParams,
} from './types';

export const useProductItemList = (params?: ProductItemListParams) => {
  const hasNameFilter = Boolean(params?.name && params.name.trim().length >= 1);
  const hasIdsFilter = Boolean(params?.ids && params.ids.length > 0);

  return useQuery<ApiResponse<ProductItem[]>, Error, ProductItem[] | undefined>({
    queryKey: PRODUCT_KEYS.itemList(params),
    queryFn: () => ProductApi.getItemList(params),
    enabled: hasNameFilter || hasIdsFilter,
    placeholderData: keepPreviousData,
    select: (data) => data.result ?? [],
  });
};

export const useProductList = (params?: RequestParams) => {
  return useQuery<ApiResponseList<ProductBase>>({
    queryKey: PRODUCT_KEYS.list(params),
    queryFn: () => ProductApi.getList(params),
    placeholderData: keepPreviousData,
  });
};

export const useProductById = (id: number) => {
  return useQuery<ApiResponse<ProductById>>({
    queryKey: PRODUCT_KEYS.detail(id),
    queryFn: () => ProductApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProductCreatePayload) => ProductApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCT_KEYS.all });
    },
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProductUpdatePayload) => ProductApi.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCT_KEYS.all });
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => ProductApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCT_KEYS.all });
    },
  });
};
